import { Typography } from '@mui/material';
import styled from 'styled-components';
import { formatShortDateTime, localizeDate } from '../../utils/datetime';
import { pluralizeString } from '../../utils/common';
import Dialog from '../Dialog';
import { FlexCenter, FlexColumn } from '../Flex';

const SamplesList = styled(FlexColumn)`
  max-height: 200px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.palette.common.gray5};
  border-radius: 4px;
  padding: 8px;
  gap: 4px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (start: number, end: number) => void;
  startTime?: number;
  endTime?: number;
  excludedSamples?: Array<{ timestamp: number }>;
};

const ExcludeConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  startTime,
  endTime,
  excludedSamples = [],
}: Props) => {
  if (!startTime || !endTime) return null;

  const formatDisplayTime = (timestamp: number) =>
    formatShortDateTime(localizeDate(timestamp));

  const sampleCount = excludedSamples.length;

  return (
    <Dialog
      open={open}
      title="Do you want to exclude this time range from the training period?"
      message={
        <FlexColumn sx={{ gap: 2 }}>
          <FlexCenter sx={{ gap: 1 }}>
            <Typography>{formatDisplayTime(startTime)}</Typography>-
            <Typography>{formatDisplayTime(endTime)}</Typography>
          </FlexCenter>

          {sampleCount > 0 && (
            <FlexColumn sx={{ gap: 1 }}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {pluralizeString(sampleCount, 'sample', 'samples')} will be
                excluded:
              </Typography>
              <SamplesList>
                {excludedSamples.map((sample, index) => (
                  <Typography
                    key={`${sample.timestamp}-${index}`}
                    sx={{
                      fontSize: '12px',
                      fontFamily: "'Geist Mono', monospace",
                    }}
                  >
                    {formatDisplayTime(sample.timestamp)}
                  </Typography>
                ))}
              </SamplesList>
            </FlexColumn>
          )}
        </FlexColumn>
      }
      onClose={onClose}
      onConfirmButtonClick={() => onConfirm(startTime, endTime)}
      confirmText="Exclude Range"
    />
  );
};

export default ExcludeConfirmationDialog;
